import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import {
  Registration,
  RegistrationProps,
} from 'src/components/tournaments/registration/Registration';
import {
  ParticipantTab,
  ParticipantTabProps,
} from 'src/components/tournaments/participants/ParticipantTab';
import { HeatsLeaderboardsTab } from 'src/components/tournaments/heats-leaderboards/HeatsLeaderboardsTab';
import {
  HeatsBracket,
  HeatsBracketTab,
} from 'src/components/tournaments/brackets/HeatsBracketTab';
import { Tournament } from 'functions/src/types/firestore/Game/Tournament';
import { useTournamentButtonGroup } from 'src/hooks/useTournamentButtonGroup';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { GradientIconButton } from '../gradients/GradientIconButton';
import { CopyTournamentForm } from '../data-entry-wizard/CopyTournamentForm';
import { useAuth } from 'src/contexts/AuthContext';
import { isAdmin } from 'functions/src/types/firestore/User/util';
import { useWizardDialog } from 'src/hooks/wizard/useWizardDialog';
import { OutcomeSubmit } from '../data-entry-wizard/OutcomeSubmit';
import { useState } from 'react';
import { BracketProvider } from './brackets/BracketProvider';
import { LottieLoader } from '../LottieLoader';
import { RefereeProvider } from 'src/contexts/RefereeContext';

export type SingleTournamentProps = ParticipantTabProps &
  Pick<HeatsBracket, 'heats'> &
  Pick<RegistrationProps, 'teams' | 'gameId'>;

export const SingleTournament = ({
  tournament,
  registration,
  waitlist,
  teams,
  gameId,
  heats,
}: SingleTournamentProps) => {
  const [isBracketLoading, setIsBracketLoading] = useState(true);

  const { activeTab, setActiveTab, TournamentButtonGroup } =
    useTournamentButtonGroup({
      phase: tournament.phase,
      bracketType: tournament.bracketType,
    });

  const { user } = useAuth();

  const { open: openWizardDialog } = useWizardDialog();
  const storeDefault = {
    message: '',
    success: false,
  };

  const onClickCopy = () => {
    openWizardDialog({
      storeDefault,
      elements: {
        NewTournamentForm: <CopyTournamentForm tournament={tournament} />,
        OutcomeSubmit: <OutcomeSubmit />,
      },
      elementIdEntry: 'NewTournamentForm',
    });
  }; 

  return (
    <RefereeProvider gameId={gameId}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pb: 4,
        }}
      >
        {TournamentButtonGroup}
        {isAdmin(user?.email) && (
          <Box sx={{ pt: 4 }}>
            <GradientIconButton
              IconComponent={ContentCopyIcon}
              gradientColor="primary.horizontal"
              onClick={onClickCopy}
            />
          </Box>
        )}
      </Box>
      {activeTab === 'BRACKET' && isBracketLoading && (
        <Stack alignItems="center" py={8}>
          <LottieLoader sx={{ height: '40px', width: '40px' }} />
        </Stack>
      )}
      {activeTab === 'BRACKET' && heats && (
        <BracketProvider {...{ tournament, heats }}>
          <HeatsBracketTab
            gameId={gameId}
            setIsBracketLoading={(state) => {
              return setIsBracketLoading(state);
            }}
          />
        </BracketProvider>
      )}
      {activeTab === 'HEATS' && heats && (
        <HeatsLeaderboardsTab
          handleTabChange={(tabName) => {
            return setActiveTab(
              tabName as 'BRACKET' | 'REGISTRATION' | 'PARTICIPANTS' | 'HEATS',
            );
          }}
          sponsors={tournament.sponsors}
          heats={heats}
          gameId={gameId}
        />
      )}
      {activeTab === 'REGISTRATION' && (
        <Registration
          {...(tournament as Tournament<Date> & {
            checkInOptions: Tournament<Date>['registrationOptions']; // TODO: Remove after March 1st
          })}
          teams={teams}
          gameId={gameId}
        />
      )}
      {activeTab === 'PARTICIPANTS' && (
        <ParticipantTab
          tournament={tournament}
          registration={registration}
          waitlist={waitlist}
        />
      )}
    </RefereeProvider>
  );
};
